import { ColDef, RowSelectedEvent } from '@ag-grid-community/core';
import { Button, Flex, useDisclosure } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhysicianModal } from '../../../components/modal/physician';
import { PhysicianDto, api } from '../../../lib/api';
import { Tag } from '@/components/ui/tag';
import { Grid } from '@/components/ui/grid';
import { formatPhone } from '@/components/ui/phone';

export const PhysiciansPage = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { open: isPhysicianModalOpen, onOpen: onPhysicianModalOpen, onClose: onPhysicianModalClose } = useDisclosure();

    const query = useQuery<PhysicianDto[]>({
        queryKey: ['physician', 'list'],
        queryFn: async () => {
            const { data } = await api.physician.list();
            return data;
        },
    });

    const [colDefs] = useState<ColDef<PhysicianDto>[]>([
        {
            field: 'npi',
            headerName: 'NPI',
            maxWidth: 140,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: false,
            resizable: true,
            pinned: 'left',
            lockPinned: true,
        },
        {
            field: 'firstName',
            maxWidth: 140,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: false,
            resizable: true,
            sort: 'asc',
        },
        { field: 'lastName', maxWidth: 140, filter: 'agTextColumnFilter', suppressHeaderFilterButton: false, resizable: false },
        {
            field: 'credential',
            headerName: 'Credentials',
            maxWidth: 140,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: true,
            resizable: false,
        },
        {
            field: 'pecos',
            headerName: 'PECOS',
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: false,
            resizable: false,
            maxWidth: 140,
            cellRenderer: ({ data }: { data: PhysicianDto }) => {
                return (
                    <Flex alignItems="center" height="100%">
                        <Tag colorPalette={data.pecos ? 'green' : 'red'} size="sm" textTransform="uppercase" fontWeight="700">
                            {data.pecos ? 'enrolled' : 'not enrolled'}
                        </Tag>
                    </Flex>
                );
            },
        },
        { field: 'city', maxWidth: 140, filter: 'agTextColumnFilter', suppressHeaderFilterButton: true, resizable: false },
        {
            field: 'phone',
            maxWidth: 140,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: true,
            resizable: false,
            valueFormatter: ({ value }) => {
                if (!value) {
                    return 'n/a';
                }

                return formatPhone(value) || 'uk';
            },
        },
        {
            field: 'fax',
            maxWidth: 140,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: true,
            resizable: false,
            valueFormatter: ({ value }) => {
                if (!value) {
                    return 'n/a';
                }

                return formatPhone(value) || 'uk';
            },
        },
    ]);

    const handleAdd = async () => {
        await queryClient.invalidateQueries({ queryKey: ['physician', 'list'] });
        onPhysicianModalClose();
    };

    const handleSelect = (event: RowSelectedEvent) => {
        if (event.node.isSelected()) {
            navigate(`/settings/physicians/${event.node.data.id}/edit`);
        }
    };

    return (
        <>
            <Flex h="40px" alignItems="center" justifyContent="space-between" pb="12px">
                <Flex>
                    <Button size="sm" onClick={() => onPhysicianModalOpen()} textDecoration="none">
                        Add Physician
                    </Button>
                </Flex>
            </Flex>
            <Flex flexDirection="column" style={{ width: '100%', height: 'calc(100% - 40px)' }}>
                <Grid
                    rowSelection="single"
                    suppressRowHoverHighlight
                    suppressCellFocus
                    columnDefs={colDefs}
                    rowData={query.data}
                    onRowSelected={handleSelect}
                />
            </Flex>

            <PhysicianModal isOpen={isPhysicianModalOpen} onClose={onPhysicianModalClose} onAdd={() => handleAdd()} />
        </>
    );
};
